import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Grid,
  Paper,
  Alert,
  Snackbar,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { API_ENDPOINT } from "../utils/constants";
import { getHeader, getMultipartHeader, getToken } from "../utils/util";

const suggestionTypes = [
  { value: "url_action", label: "URL Action" },
  { value: "dialer_action", label: "Dialer Action" },
  { value: "share_location", label: "Share Location" },
  { value: "reply", label: "Reply" },
  { value: "calendar_event", label: "Calendar Event" },
  { value: "view_location_latlong", label: "View Location" },
  { value: "request_location", label: "Request Location" },
  { value: "copy_code", label: "Copy Code" },
];

const cardOrientationOptions = [
  { value: "HORIZONTAL", label: "Horizontal" },
  { value: "VERTICAL", label: "Vertical" },
];

const heightOptions = [
  { value: "SHORT", label: "Short" },
  { value: "MEDIUM", label: "Medium" },
  { value: "TALL", label: "Tall" },
];

const alignmentOptions = [
  { value: "LEFT", label: "Left" },
  { value: "RIGHT", label: "Right" },
];

const fileTypeOptions = [
  { value: "IMAGE", label: "Image" },
  { value: "VIDEO", label: "Video" },
  { value: "AUDIO", label: "Audio" },
  { value: "DOCUMENT", label: "Document" },
];

// New Reusable Guidelines Component for improved UX
const MediaGuidelines = () => (
  <Grid item xs={12}>
    <Alert severity="info" variant="standard" sx={{ mb: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
        Media & Thumbnail Requirements
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={4}>
          <Typography variant="body2" fontWeight="bold" color="primary.main">
            Image (JPEG, JPG, PNG, GIF)
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, pl: 2, typography: "caption" }}>
            <li>
              <strong>Short:</strong> 1440 x 480 pixels
            </li>
            <li>
              <strong>Medium:</strong> 1440 x 720 pixels
            </li>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="body2" fontWeight="bold" color="primary.main">
            Video (MP4, Max 5MB)
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, pl: 2, typography: "caption" }}>
            <li>
              <strong>Short:</strong> 1440 x 480 pixels
            </li>
            <li>
              <strong>Medium:</strong> 1440 x 720 pixels
            </li>
          </Box>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Typography variant="body2" fontWeight="bold" color="primary.main">
            Thumbnail (Below 100KB)
          </Typography>
          <Box component="ul" sx={{ mt: 0.5, pl: 2, typography: "caption" }}>
            <li>
              <strong>Short:</strong> 1440 x 480 pixels
            </li>
            <li>
              <strong>Medium:</strong> 980 x 420 pixels
            </li>
          </Box>
        </Grid>
      </Grid>
    </Alert>
  </Grid>
);

const RCSTemplateCreator = () => {
  const [templateType, setTemplateType] = useState("text_message");
  const [templateName, setTemplateName] = useState("");
  const [userId, setUserId] = useState("Canny_Digital2");

  // Text message state
  const [messageText, setMessageText] = useState("");

  // Rich card state
  const [cardOrientation, setCardOrientation] = useState("HORIZONTAL");
  const [cardTitle, setCardTitle] = useState("");
  const [cardDescription, setCardDescription] = useState("");
  const [cardFile, setCardFile] = useState(null);
  const [cardThumbnail, setCardThumbnail] = useState(null);
  const [fileType, setFileType] = useState("IMAGE");
  const [mediaHeight, setMediaHeight] = useState("SHORT");
  const [alignment, setAlignment] = useState("LEFT");

  // Text with media state
  const [documentFileName, setDocumentFileName] = useState("");
  const [messageOrder, setMessageOrder] = useState("text_message_at_top");
  const [mediaFile, setMediaFile] = useState(null);

  // Carousel state
  const [carouselCards, setCarouselCards] = useState([
    {
      title: "",
      description: "",
      file: null,
      thumbnail: null,
      fileType: "IMAGE",
      height: "MEDIUM",
      suggestions: [],
    },
  ]);
  const [cardWidth, setCardWidth] = useState("MEDIUM");

  // Suggestions state
  const [suggestions, setSuggestions] = useState([]);

  // Quick reply state
  const [quickReplyButtons, setQuickReplyButtons] = useState([]);

  // UI state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mediaFile) {
      setDocumentFileName(mediaFile.name);
    }
  }, [mediaFile]);

  useEffect(() => {
    if (cardFile) {
      setDocumentFileName(cardFile.name);
    }
  }, [cardFile]);

  const showSnackbar = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const addSuggestion = (targetArray, setTargetArray) => {
    const newSuggestion = {
      suggestionType: "reply",
      text: "",
      postbackData: `[message_id]`,
    };
    setTargetArray([...targetArray, newSuggestion]);
  };

  const updateSuggestion = (
    index,
    field,
    value,
    targetArray,
    setTargetArray
  ) => {
    const updated = [...targetArray];
    updated[index] = { ...updated[index], [field]: value };
    setTargetArray(updated);
  };

  const removeSuggestion = (index, targetArray, setTargetArray) => {
    setTargetArray(targetArray.filter((_, i) => i !== index));
  };

  const addCarouselCard = () => {
    if (carouselCards.length >= 10) {
      showSnackbar("Maximum of 10 cards allowed in carousel", "warning");
      return;
    }
    setCarouselCards([
      ...carouselCards,
      {
        title: "",
        description: "",
        file: null,
        thumbnail: null,
        fileType: "IMAGE",
        height: "MEDIUM",
        suggestions: [],
      },
    ]);
  };

  const updateCarouselCard = (index, field, value) => {
    const updated = [...carouselCards];
    updated[index] = { ...updated[index], [field]: value };
    setCarouselCards(updated);
  };

  const removeCarouselCard = (index) => {
    setCarouselCards(carouselCards.filter((_, i) => i !== index));
  };

  const addQuickReplyButton = () => {
    setQuickReplyButtons([
      ...quickReplyButtons,
      {
        text: "",
        postbackData: "[message_id]",
      },
    ]);
  };

  const buildPayload = () => {
    const base = {
      templateName,
      templateType,
    };

    if (
      ["text_message_with_media", "text_message_with_pdf"].includes(
        templateType
      )
    ) {
      base.userId = userId;
    }

    switch (templateType) {
      case "text_message":
        return {
          ...base,
          message: {
            contentMessage: {
              text: messageText,
              suggestions:
                suggestions.length > 0
                  ? suggestions.map((s) => ({ action: s }))
                  : [],
            },
          },
        };

      case "rich_card":
        return {
          ...base,
          message: {
            contentMessage: {
              richCard: {
                standaloneCard: {
                  cardOrientation,
                  cardContent: {
                    ...(cardOrientation === "VERTICAL"
                      ? {}
                      : { alignment: alignment }),
                    title: cardTitle,
                    description: cardDescription,
                    fileName: cardFile?.name || "",
                    thumbnailFileName: cardThumbnail?.name || "",
                    media: {
                      contentInfo: {},
                      fileType,
                      ...(cardOrientation === "VERTICAL"
                        ? { height: mediaHeight }
                        : { alignment: alignment }),
                    },
                    suggestions:
                      suggestions.length > 0
                        ? suggestions.map((s) => ({ action: s }))
                        : [],
                  },
                },
              },
            },
          },
        };

      case "carousel":
        return {
          ...base,
          message: {
            contentMessage: {
              richCard: {
                carouselCard: {
                  cardWidth,
                  cardContents: carouselCards.map((card) => ({
                    title: card.title,
                    description: card.description,
                    fileName: card.file?.name || "",
                    thumbnailFileName: card.thumbnail?.name || "",
                    media: {
                      contentInfo: {},
                      fileType: card.fileType,
                      height: card.height,
                    },
                    suggestions:
                      card.suggestions.length > 0
                        ? card.suggestions.map((s) => ({ action: s }))
                        : [],
                  })),
                },
              },
            },
          },
        };

      case "text_message_with_media":
      case "text_message_with_pdf":
        return {
          ...base,
          userId,
          documentFileName: mediaFile?.name || documentFileName,
          messageOrder,
          message: {
            contentMessage: {
              text: messageText,
              suggestions:
                suggestions.length > 0
                  ? suggestions.map((s) => ({ action: s }))
                  : [],
            },
          },
        };

      case "quick_reply":
        return {
          ...base,
          message: {
            contentMessage: {
              text: messageText,
              quickReply: {
                quickReplyButtons: quickReplyButtons,
              },
              suggestions:
                suggestions.length > 0
                  ? suggestions.map((s) => ({ action: s }))
                  : [],
            },
          },
        };

      default:
        return base;
    }
  };

  const collectFiles = () => {
    const files = [];

    if (templateType === "rich_card") {
      if (cardFile) files.push(cardFile);
      if (cardThumbnail) files.push(cardThumbnail);
    } else if (templateType === "carousel") {
      carouselCards.forEach((card) => {
        if (card.file) files.push(card.file);
        if (card.thumbnail) files.push(card.thumbnail);
      });
    } else if (
      ["text_message_with_media", "text_message_with_pdf"].includes(
        templateType
      )
    ) {
      if (mediaFile) files.push(mediaFile);
    }

    return files;
  };

  const validateFile = (file, isThumbnail = false, fileType = "IMAGE") => {
    if (!file) return true;

    const imageFormats = ["image/jpeg", "image/png", "image/gif"];
    const videoFormats = ["video/mp4", "video/x-msvideo", "video/quicktime"];

    let allowedFormats = [];
    let maxSize = 0;

    if (fileType === "IMAGE") {
      allowedFormats = imageFormats;
      maxSize = 1 * 1024 * 1024; // 1 MB
    } else if (fileType === "VIDEO") {
      allowedFormats = videoFormats;
      maxSize = 5 * 1024 * 1024; // 5 MB
    }

    if (isThumbnail) {
      maxSize = 100 * 1024; // 100 kB max
      if (file.size < 40 * 1024) return "Thumbnail must be at least 40 kB";
      allowedFormats = imageFormats;
    }

    if (!allowedFormats.includes(file.type)) {
      return "Invalid file format. Allowed: JPEG, PNG, GIF for images; MP4, AVI, MOV for videos.";
    }

    if (file.size > maxSize) {
      return `File too large. Max size: ${
        isThumbnail ? "100 kB" : fileType === "IMAGE" ? "1 MB" : "5 MB"
      }`;
    }

    return true;
  };

  const validateForm = () => {
    const newErrors = {};
    const maxTemplateName = 20;
    const maxTitle = 200;
    const maxDescriptionText = 2500;
    const maxDescriptionRich = 2000;
    const maxSuggestionText = 25;

    if (!templateName) newErrors.templateName = "Template name is required";
    else if (templateName.length > maxTemplateName)
      newErrors.templateName = `Max ${maxTemplateName} characters`;

    if (
      ["text_message_with_media", "text_message_with_pdf"].includes(
        templateType
      )
    ) {
      if (!userId)
        newErrors.userId = "User ID is required for this template type";
      if (!mediaFile) newErrors.mediaFile = "Please upload a media file";
    }

    if (
      [
        "text_message",
        "text_message_with_media",
        "text_message_with_pdf",
        "quick_reply",
      ].includes(templateType)
    ) {
      if (!messageText) newErrors.messageText = "Message text is required";
      else if (messageText.length > maxDescriptionText)
        newErrors.messageText = `Max ${maxDescriptionText} characters`;
    }

    if (templateType === "rich_card") {
      if (!cardTitle) newErrors.cardTitle = "Card title is required";
      else if (cardTitle.length > maxTitle)
        newErrors.cardTitle = `Max ${maxTitle} characters`;
      if (!cardDescription)
        newErrors.cardDescription = "Card description is required";
      else if (cardDescription.length > maxDescriptionRich)
        newErrors.cardDescription = `Max ${maxDescriptionRich} characters`;
      if (fileType === "VIDEO" && !cardThumbnail)
        newErrors.cardThumbnail = "Thumbnail is required for video";

      if (cardFile) {
        const fileValid = validateFile(cardFile, false, fileType);
        if (fileValid !== true) newErrors.cardFile = fileValid;
      }
      if (cardThumbnail) {
        const thumbValid = validateFile(cardThumbnail, true);
        if (thumbValid !== true) newErrors.cardThumbnail = thumbValid;
      }
    }

    if (templateType === "carousel") {
      if (carouselCards.length < 2)
        newErrors.carousel = "Minimum 2 cards required for carousel";
      if (carouselCards.length > 10)
        newErrors.carousel = "Maximum 10 cards allowed for carousel";
      carouselCards.forEach((card, index) => {
        if (!card.title)
          newErrors[`card${index}Title`] = `Card ${index + 1} title required`;
        else if (card.title.length > maxTitle)
          newErrors[`card${index}Title`] = `Max ${maxTitle} characters`;
        if (card.description && card.description.length > maxDescriptionRich)
          newErrors[
            `card${index}Description`
          ] = `Max ${maxDescriptionRich} characters`;
        if (card.file) {
          const fileValid = validateFile(card.file, false, card.fileType);
          if (fileValid !== true) newErrors[`card${index}File`] = fileValid;
        }
        if (card.thumbnail) {
          const thumbValid = validateFile(card.thumbnail, true);
          if (thumbValid !== true)
            newErrors[`card${index}Thumbnail`] = thumbValid;
        }
      });
    }

    suggestions.forEach((sugg, index) => {
      if (sugg.text && sugg.text.length > maxSuggestionText)
        newErrors[
          `sugg${index}Text`
        ] = `Max ${maxSuggestionText} characters for suggestion text`;
    });
    carouselCards.forEach((card) => {
      card.suggestions.forEach((sugg, index) => {
        if (sugg.text && sugg.text.length > maxSuggestionText)
          newErrors[
            `cardSugg${index}Text`
          ] = `Max ${maxSuggestionText} characters for suggestion text`;
      });
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar("Please fix validation errors", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = buildPayload();
      const files = collectFiles();

      const formData = new FormData();
      formData.append("data", JSON.stringify(payload));
      files.forEach((file) => {
        formData.append("files", file);
      });

      console.log("Payload:", payload);
      console.log("Files:", files);

      const response = await fetch(`${API_ENDPOINT}/multipart/add-template-2`, {
        method: "POST",
        body: formData,
        headers: {
          authorization: `Bearer ${getToken()}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        showSnackbar(
          result.message || "Template created successfully!",
          "success"
        );
        console.log("API Response:", result);
        resetForm();
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.error || errorData.message || `HTTP ${response.status}`
        );
      }
    } catch (error) {
      showSnackbar(`Error: ${error.message}`, "error");
      console.error("Submission error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTemplateName("");
    setUserId("Canny_Digital2");
    setMessageText("");
    setCardTitle("");
    setCardDescription("");
    setCardFile(null);
    setCardThumbnail(null);
    setMediaFile(null);
    setSuggestions([]);
    setQuickReplyButtons([]);
    setCarouselCards([
      {
        title: "",
        description: "",
        file: null,
        thumbnail: null,
        fileType: "IMAGE",
        height: "MEDIUM",
        suggestions: [],
      },
    ]);
  };

  const renderSuggestionFields = (
    suggestion,
    index,
    targetArray,
    setTargetArray
  ) => {
    const fields = {
      url_action: (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              label="URL"
              value={suggestion.openUrlAction?.url || ""}
              onChange={(e) => {
                const updated = [...targetArray];
                updated[index] = {
                  ...updated[index],
                  openUrlAction: { url: e.target.value },
                };
                setTargetArray(updated);
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Application</InputLabel>
              <Select
                value={suggestion.application || "Webview"}
                onChange={(e) => {
                  const updated = [...targetArray];
                  updated[index] = {
                    ...updated[index],
                    application: e.target.value,
                  };
                  setTargetArray(updated);
                }}
                label="Application"
              >
                <MenuItem value="Webview">Webview</MenuItem>
                <MenuItem value="Browser">Browser</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>View Mode</InputLabel>
              <Select
                value={suggestion.webviewViewMode || "TALL"}
                onChange={(e) => {
                  const updated = [...targetArray];
                  updated[index] = {
                    ...updated[index],
                    webviewViewMode: e.target.value,
                  };
                  setTargetArray(updated);
                }}
                label="View Mode"
              >
                <MenuItem value="TALL">Tall</MenuItem>
                <MenuItem value="HALF">Half</MenuItem>
                <MenuItem value="FULL">Full</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      ),
      dialer_action: (
        <TextField
          label="Phone Number"
          value={suggestion.dialAction?.phoneNumber || ""}
          onChange={(e) => {
            const updated = [...targetArray];
            updated[index] = {
              ...updated[index],
              dialAction: { phoneNumber: e.target.value },
            };
            setTargetArray(updated);
          }}
          fullWidth
          size="small"
        />
      ),
      calendar_event: (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Event Title"
              value={suggestion.createCalendarEventAction?.title || ""}
              onChange={(e) => {
                const updated = [...targetArray];
                updated[index] = {
                  ...updated[index],
                  createCalendarEventAction: {
                    ...updated[index].createCalendarEventAction,
                    title: e.target.value,
                  },
                };
                setTargetArray(updated);
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={suggestion.createCalendarEventAction?.description || ""}
              onChange={(e) => {
                const updated = [...targetArray];
                updated[index] = {
                  ...updated[index],
                  createCalendarEventAction: {
                    ...updated[index].createCalendarEventAction,
                    description: e.target.value,
                  },
                };
                setTargetArray(updated);
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="Start Time"
              placeholder="2025-01-01 00:00:00"
              value={suggestion.createCalendarEventAction?.startTime || ""}
              onChange={(e) => {
                const updated = [...targetArray];
                updated[index] = {
                  ...updated[index],
                  createCalendarEventAction: {
                    ...updated[index].createCalendarEventAction,
                    startTime: e.target.value,
                  },
                };
                setTargetArray(updated);
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              label="End Time"
              placeholder="2025-01-31 00:00:00"
              value={suggestion.createCalendarEventAction?.endTime || ""}
              onChange={(e) => {
                const updated = [...targetArray];
                updated[index] = {
                  ...updated[index],
                  createCalendarEventAction: {
                    ...updated[index].createCalendarEventAction,
                    endTime: e.target.value,
                  },
                };
                setTargetArray(updated);
              }}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      ),
      view_location_latlong: (
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <TextField
              label="Latitude"
              type="number"
              value={suggestion.viewLocationAction?.latLong?.latitude || ""}
              onChange={(e) => {
                const updated = [...targetArray];
                updated[index] = {
                  ...updated[index],
                  viewLocationAction: {
                    latLong: {
                      ...updated[index].viewLocationAction?.latLong,
                      latitude: parseFloat(e.target.value),
                    },
                    label: updated[index].viewLocationAction?.label || "",
                  },
                };
                setTargetArray(updated);
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Longitude"
              type="number"
              value={suggestion.viewLocationAction?.latLong?.longitude || ""}
              onChange={(e) => {
                const updated = [...targetArray];
                updated[index] = {
                  ...updated[index],
                  viewLocationAction: {
                    latLong: {
                      ...updated[index].viewLocationAction?.latLong,
                      longitude: parseFloat(e.target.value),
                    },
                    label: updated[index].viewLocationAction?.label || "",
                  },
                };
                setTargetArray(updated);
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              label="Location Label"
              value={suggestion.viewLocationAction?.label || ""}
              onChange={(e) => {
                const updated = [...targetArray];
                updated[index] = {
                  ...updated[index],
                  viewLocationAction: {
                    ...updated[index].viewLocationAction,
                    label: e.target.value,
                  },
                };
                setTargetArray(updated);
              }}
              fullWidth
              size="small"
            />
          </Grid>
        </Grid>
      ),
      copy_code: (
        <TextField
          label="Code to Copy"
          value={suggestion.copyCode?.code || ""}
          onChange={(e) => {
            const updated = [...targetArray];
            updated[index] = {
              ...updated[index],
              copyCode: { code: e.target.value },
            };
            setTargetArray(updated);
          }}
          fullWidth
          size="small"
        />
      ),
    };

    return fields[suggestion.suggestionType] || null;
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 1 }}>
        RCS Template Creator
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Create and upload RCS message templates with rich media. Templates must
        be approved by Vi RBM Admin before use.
      </Typography>

      {/* Basic Information */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Template Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Template Type</InputLabel>
                <Select
                  value={templateType}
                  onChange={(e) => {
                    setTemplateType(e.target.value);
                    setErrors({});
                  }}
                  label="Template Type"
                >
                  <MenuItem value="text_message">Text Message</MenuItem>
                  <MenuItem value="rich_card">Rich Card</MenuItem>
                  <MenuItem value="carousel">Carousel</MenuItem>
                  <MenuItem value="text_message_with_media">
                    Text with Media
                  </MenuItem>
                  <MenuItem value="text_message_with_pdf">
                    Text with PDF
                  </MenuItem>
                  <MenuItem value="quick_reply">Quick Reply</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Template Name *"
                value={templateName}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z0-9_]/g, "");
                  setTemplateName(value);
                }}
                fullWidth
                size="small"
                error={!!errors.templateName}
                helperText={
                  errors.templateName ||
                  "Only letters, numbers, underscore. Max 20 characters"
                }
                inputProps={{ maxLength: 20 }}
              />
            </Grid>
            {["text_message_with_media", "text_message_with_pdf"].includes(
              templateType
            ) && (
              <Grid item xs={12} md={4}>
                <TextField
                  label="User ID *"
                  value={userId}
                  disabled
                  onChange={(e) => setUserId(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.userId}
                  helperText={errors.userId || "Default: Canny_Digital2"}
                />
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* Content Configuration */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Content
          </Typography>

          {[
            "text_message",
            "text_message_with_media",
            "text_message_with_pdf",
            "quick_reply",
          ].includes(templateType) && (
            <TextField
              label="Message Text *"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              fullWidth
              multiline
              rows={4}
              error={!!errors.messageText}
              helperText={
                errors.messageText ||
                "Max 2500 characters. Supports personalization variables like [DISCOUNT] or [CODE]"
              }
              sx={{ mb: 2 }}
              inputProps={{ maxLength: 2500 }}
            />
          )}

          {["text_message_with_media", "text_message_with_pdf"].includes(
            templateType
          ) && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Message Order</InputLabel>
                  <Select
                    value={messageOrder}
                    onChange={(e) => setMessageOrder(e.target.value)}
                    label="Message Order"
                  >
                    <MenuItem value="text_message_at_top">
                      Text Message at Top
                    </MenuItem>
                    <MenuItem value="media_at_top">Media at top</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                >
                  Upload{" "}
                  {templateType === "text_message_with_pdf" ? "PDF" : "Media"}{" "}
                  File *
                  <input
                    type="file"
                    hidden
                    accept={
                      templateType === "text_message_with_pdf"
                        ? ".pdf"
                        : "image/*,video/*"
                    }
                    onChange={(e) => setMediaFile(e.target.files[0])}
                  />
                </Button>
                {mediaFile && (
                  <Chip
                    label={mediaFile.name}
                    onDelete={() => setMediaFile(null)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
                {errors.mediaFile && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.mediaFile}
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}

          {templateType === "rich_card" && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Card Orientation</InputLabel>
                  <Select
                    value={cardOrientation}
                    onChange={(e) => setCardOrientation(e.target.value)}
                    label="Card Orientation"
                  >
                    {cardOrientationOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>File Type</InputLabel>
                  <Select
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                    label="File Type"
                  >
                    {fileTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Title *"
                  value={cardTitle}
                  onChange={(e) => setCardTitle(e.target.value)}
                  fullWidth
                  size="small"
                  error={!!errors.cardTitle}
                  helperText={
                    errors.cardTitle ||
                    "Max 200 characters. Supports personalization variables like [DISCOUNT]"
                  }
                  inputProps={{ maxLength: 200 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Description *"
                  value={cardDescription}
                  onChange={(e) => setCardDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  error={!!errors.cardDescription}
                  helperText={
                    errors.cardDescription ||
                    "Max 2000 characters. Supports personalization variables like [CODE]"
                  }
                  inputProps={{ maxLength: 2000 }}
                />
              </Grid>

              {/* Added Guidelines here for better context before uploading */}
              <MediaGuidelines />

              <Grid item xs={12} md={4}>
                {cardOrientation === "VERTICAL" ? (
                  <FormControl fullWidth size="small">
                    <InputLabel>Media Height</InputLabel>
                    <Select
                      value={mediaHeight}
                      onChange={(e) => setMediaHeight(e.target.value)}
                      label="Media Height"
                    >
                      {heightOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ) : (
                  <FormControl fullWidth size="small">
                    <InputLabel>Alignment</InputLabel>
                    <Select
                      value={alignment}
                      onChange={(e) => setAlignment(e.target.value)}
                      label="Alignment"
                    >
                      {alignmentOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                >
                  Upload Media File
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setCardFile(e.target.files[0])}
                  />
                </Button>
                {cardFile && (
                  <Chip
                    label={cardFile.name}
                    onDelete={() => setCardFile(null)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
                {errors.cardFile && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.cardFile}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  variant="outlined"
                  component="label"
                  fullWidth
                  startIcon={<CloudUploadIcon />}
                >
                  Upload Thumbnail
                  <input
                    type="file"
                    hidden
                    onChange={(e) => setCardThumbnail(e.target.files[0])}
                  />
                </Button>
                {cardThumbnail && (
                  <Chip
                    label={cardThumbnail.name}
                    onDelete={() => setCardThumbnail(null)}
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
                {errors.cardThumbnail && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block" }}
                  >
                    {errors.cardThumbnail}
                  </Typography>
                )}
              </Grid>
            </Grid>
          )}

          {templateType === "carousel" && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <MediaGuidelines />
              </Box>

              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
              >
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Card Width</InputLabel>
                  <Select
                    value={cardWidth}
                    onChange={(e) => setCardWidth(e.target.value)}
                    label="Card Width"
                  >
                    <MenuItem value="SMALL">Small</MenuItem>
                    <MenuItem value="MEDIUM">Medium</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addCarouselCard}
                  size="small"
                >
                  Add Card
                </Button>
              </Box>

              {carouselCards.map((card, cardIndex) => (
                <Accordion key={cardIndex} defaultExpanded={cardIndex === 0}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>
                      Card {cardIndex + 1} - {card.title || "Untitled"}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <TextField
                          label="Title *"
                          value={card.title}
                          onChange={(e) =>
                            updateCarouselCard(
                              cardIndex,
                              "title",
                              e.target.value
                            )
                          }
                          fullWidth
                          size="small"
                          error={!!errors[`card${cardIndex}Title`]}
                          helperText={
                            errors[`card${cardIndex}Title`] ||
                            "Max 200 characters. Supports personalization variables like [DISCOUNT]"
                          }
                          inputProps={{ maxLength: 200 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Description"
                          value={card.description}
                          onChange={(e) =>
                            updateCarouselCard(
                              cardIndex,
                              "description",
                              e.target.value
                            )
                          }
                          fullWidth
                          multiline
                          rows={2}
                          size="small"
                          error={!!errors[`card${cardIndex}Description`]}
                          helperText={
                            errors[`card${cardIndex}Description`] ||
                            "Max 2000 characters. Supports personalization variables like [CODE]"
                          }
                          inputProps={{ maxLength: 2000 }}
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>File Type</InputLabel>
                          <Select
                            value={card.fileType}
                            onChange={(e) =>
                              updateCarouselCard(
                                cardIndex,
                                "fileType",
                                e.target.value
                              )
                            }
                            label="File Type"
                          >
                            {fileTypeOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Height</InputLabel>
                          <Select
                            value={card.height}
                            onChange={(e) =>
                              updateCarouselCard(
                                cardIndex,
                                "height",
                                e.target.value
                              )
                            }
                            label="Height"
                          >
                            {heightOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={4}></Grid>
                      <Grid item xs={12} md={6}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<AttachFileIcon />}
                          size="small"
                        >
                          Upload Media
                          <input
                            type="file"
                            hidden
                            onChange={(e) =>
                              updateCarouselCard(
                                cardIndex,
                                "file",
                                e.target.files[0]
                              )
                            }
                          />
                        </Button>
                        {card.file && (
                          <Chip
                            label={card.file.name}
                            onDelete={() =>
                              updateCarouselCard(cardIndex, "file", null)
                            }
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                        {errors[`card${cardIndex}File`] && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 1, display: "block" }}
                          >
                            {errors[`card${cardIndex}File`]}
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Button
                          variant="outlined"
                          component="label"
                          fullWidth
                          startIcon={<AttachFileIcon />}
                          size="small"
                        >
                          Upload Thumbnail
                          <input
                            type="file"
                            hidden
                            onChange={(e) =>
                              updateCarouselCard(
                                cardIndex,
                                "thumbnail",
                                e.target.files[0]
                              )
                            }
                          />
                        </Button>
                        {card.thumbnail && (
                          <Chip
                            label={card.thumbnail.name}
                            onDelete={() =>
                              updateCarouselCard(cardIndex, "thumbnail", null)
                            }
                            size="small"
                            sx={{ mt: 1 }}
                          />
                        )}
                        {errors[`card${cardIndex}Thumbnail`] && (
                          <Typography
                            variant="caption"
                            color="error"
                            sx={{ mt: 1, display: "block" }}
                          >
                            {errors[`card${cardIndex}Thumbnail`]}
                          </Typography>
                        )}
                      </Grid>

                      {/* Card-specific suggestions */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2">
                            Card Suggestions
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => {
                              const updated = [...carouselCards];
                              addSuggestion(
                                updated[cardIndex].suggestions,
                                (newSugg) => {
                                  updated[cardIndex].suggestions = newSugg;
                                  setCarouselCards(updated);
                                }
                              );
                            }}
                          >
                            Add
                          </Button>
                        </Box>
                        {card.suggestions.map((suggestion, suggIndex) => (
                          <Paper
                            key={suggIndex}
                            sx={{ p: 2, mb: 1 }}
                            elevation={0}
                          >
                            <Grid container spacing={1} alignItems="center">
                              <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                  <InputLabel>Type</InputLabel>
                                  <Select
                                    value={suggestion.suggestionType}
                                    onChange={(e) => {
                                      const updated = [...carouselCards];
                                      updated[cardIndex].suggestions[
                                        suggIndex
                                      ].suggestionType = e.target.value;
                                      setCarouselCards(updated);
                                    }}
                                    label="Type"
                                  >
                                    {suggestionTypes.map((type) => (
                                      <MenuItem
                                        key={type.value}
                                        value={type.value}
                                      >
                                        {type.label}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  label="Button Text"
                                  value={suggestion.text}
                                  onChange={(e) => {
                                    const updated = [...carouselCards];
                                    updated[cardIndex].suggestions[
                                      suggIndex
                                    ].text = e.target.value;
                                    setCarouselCards(updated);
                                  }}
                                  fullWidth
                                  size="small"
                                  helperText="Max 25 characters"
                                  inputProps={{ maxLength: 25 }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={4}>
                                <TextField
                                  label="Postback Data"
                                  value={suggestion.postbackData}
                                  onChange={(e) => {
                                    const updated = [...carouselCards];
                                    updated[cardIndex].suggestions[
                                      suggIndex
                                    ].postbackData = e.target.value;
                                    setCarouselCards(updated);
                                  }}
                                  fullWidth
                                  size="small"
                                />
                              </Grid>
                              <Grid item xs={12} sm={1}>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => {
                                    const updated = [...carouselCards];
                                    updated[cardIndex].suggestions.splice(
                                      suggIndex,
                                      1
                                    );
                                    setCarouselCards(updated);
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                              <Grid item xs={12}>
                                {renderSuggestionFields(
                                  suggestion,
                                  suggIndex,
                                  card.suggestions,
                                  (newSuggestions) => {
                                    const updated = [...carouselCards];
                                    updated[cardIndex].suggestions =
                                      newSuggestions;
                                    setCarouselCards(updated);
                                  }
                                )}
                              </Grid>
                            </Grid>
                          </Paper>
                        ))}
                      </Grid>

                      <Grid item xs={12}>
                        <Button
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => removeCarouselCard(cardIndex)}
                          size="small"
                          disabled={carouselCards.length <= 2}
                        >
                          Remove Card
                        </Button>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
              {errors.carousel && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {errors.carousel}
                </Alert>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Quick Reply Buttons */}
      {templateType === "quick_reply" && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Quick Reply Buttons</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addQuickReplyButton}
                size="small"
              >
                Add Button
              </Button>
            </Box>

            {quickReplyButtons.map((button, index) => (
              <Paper key={index} elevation={1} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={5}>
                    <TextField
                      label="Button Text"
                      value={button.text}
                      onChange={(e) => {
                        const updated = [...quickReplyButtons];
                        updated[index].text = e.target.value;
                        setQuickReplyButtons(updated);
                      }}
                      fullWidth
                      size="small"
                      helperText="Max 25 characters"
                      inputProps={{ maxLength: 25 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Postback Data"
                      value={button.postbackData}
                      onChange={(e) => {
                        const updated = [...quickReplyButtons];
                        updated[index].postbackData = e.target.value;
                        setQuickReplyButtons(updated);
                      }}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton
                      color="error"
                      onClick={() =>
                        setQuickReplyButtons(
                          quickReplyButtons.filter((_, i) => i !== index)
                        )
                      }
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Suggestions (for most template types) */}
      {!["carousel"].includes(templateType) && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Suggestions (Action Buttons)</Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => addSuggestion(suggestions, setSuggestions)}
                size="small"
              >
                Add Suggestion
              </Button>
            </Box>

            {suggestions.map((suggestion, index) => (
              <Paper key={index} elevation={1} sx={{ p: 2, mb: 2 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select
                        value={suggestion.suggestionType}
                        onChange={(e) =>
                          updateSuggestion(
                            index,
                            "suggestionType",
                            e.target.value,
                            suggestions,
                            setSuggestions
                          )
                        }
                        label="Type"
                      >
                        {suggestionTypes.map((type) => (
                          <MenuItem key={type.value} value={type.value}>
                            {type.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Button Text"
                      value={suggestion.text}
                      onChange={(e) =>
                        updateSuggestion(
                          index,
                          "text",
                          e.target.value,
                          suggestions,
                          setSuggestions
                        )
                      }
                      fullWidth
                      size="small"
                      helperText="Max 25 characters"
                      inputProps={{ maxLength: 25 }}
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label="Postback Data"
                      value={suggestion.postbackData}
                      onChange={(e) =>
                        updateSuggestion(
                          index,
                          "postbackData",
                          e.target.value,
                          suggestions,
                          setSuggestions
                        )
                      }
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} md={1}>
                    <IconButton
                      color="error"
                      onClick={() =>
                        removeSuggestion(index, suggestions, setSuggestions)
                      }
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                  <Grid item xs={12}>
                    {renderSuggestionFields(
                      suggestion,
                      index,
                      suggestions,
                      setSuggestions
                    )}
                  </Grid>
                </Grid>
              </Paper>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Submit Button */}
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        <Button
          variant="contained"
          size="large"
          onClick={handleSubmit}
          disabled={isSubmitting}
          startIcon={
            isSubmitting ? <CircularProgress size={20} /> : <SendIcon />
          }
        >
          {isSubmitting ? "Creating Template..." : "Create Template"}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RCSTemplateCreator;
